import { useRef, useState, useEffect } from "react";

import styles from "./ImageCarousel.module.css";

const MAX_IMAGES = 6;

type Props = {
  imagePreviews: string[];
  totalImages: number;
  onRemove: (index: number) => void;
  onAddMore: () => void;
};

const ImageCarousel = ({
  imagePreviews,
  totalImages,
  onRemove,
  onAddMore,
}: Props) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const canAddMore = totalImages > 0 && totalImages < MAX_IMAGES;
  // +1 slot for the "add more" card when applicable
  const totalSlides = imagePreviews.length + (canAddMore ? 1 : 0);

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    // card width (80) + gap (10) = 90
    const index = Math.round(track.scrollLeft / 90);
    setActiveIndex(index);
  };

  // Redirect vertical mouse wheel to horizontal scroll.
  // Depends on imagePreviews so it re-runs after the component mounts
  // with content (trackRef is null on the first render with no images).
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 && e.deltaX === 0) return;
      e.preventDefault();
      track.scrollLeft += e.deltaY + e.deltaX;
    };

    track.addEventListener("wheel", handleWheel, { passive: false });
    return () => track.removeEventListener("wheel", handleWheel);
  }, [imagePreviews]);

  // Reset active dot when images change (e.g. removal)
  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, totalSlides - 1));
  }, [totalSlides]);

  if (imagePreviews.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      {/* Scrollable track */}
      <div ref={trackRef} className={styles.track} onScroll={handleScroll}>
        {imagePreviews.map((src, index) => (
          <div key={index} className={styles.card}>
            <img
              src={src}
              alt={`Preview ${index + 1}`}
              className={styles.cardImage}
            />

            {/* Remove button */}
            <button
              className={styles.removeButton}
              onClick={() => onRemove(index)}
              aria-label={`Remove image ${index + 1}`}
            >
              <i className="fa-solid fa-xmark" />
            </button>

            {/* Counter badge */}
            <span className={styles.badge}>
              {index + 1}/{totalImages}
            </span>
          </div>
        ))}

        {/* Add-more card */}
        {canAddMore && (
          <button
            className={styles.addCard}
            onClick={onAddMore}
            aria-label="Add more photos"
          >
            <i className="fa-solid fa-plus" />
            <span className={styles.addCardText}>Add photo</span>
            <span className={styles.addCardSubtext}>
              {totalImages}/{MAX_IMAGES}
            </span>
          </button>
        )}
      </div>

      {/* Dot indicators */}
      {totalSlides > 1 && (
        <div className={styles.dots}>
          {Array.from({ length: totalSlides }).map((_, i) => (
            <span
              key={i}
              className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
